import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { WEEKDAYS, WeeklyOff, weeklyOffsApi } from '../../payroll.api';
import CrudTable from './CrudTable';

interface Draft {
  dayOfWeek: number;
  weeksOfMonth: string;
  active: boolean;
}

const ORDINAL = ['', '1st', '2nd', '3rd', '4th', '5th'];
const weeksLabel = (w: string) =>
  !w || w.toUpperCase() === 'ALL'
    ? 'Every week'
    : w
        .split(',')
        .map((n) => ORDINAL[Number(n.trim())] || n.trim())
        .join(' & ');

/** Weekly offs: Sunday every week, or e.g. the 2nd and 4th Saturday. */
export default function WeeklyOffsTab({ canEdit }: { canEdit: boolean }) {
  return (
    <Stack gap={1}>
      <Typography variant='body2' color='text.secondary'>
        A weekly off is a paid day without attendance. “Weeks” counts the day’s occurrences in the month: 2,4 means the 2nd and 4th.
      </Typography>
      <CrudTable<WeeklyOff, Draft>
        entityName='Weekly off'
        client={weeklyOffsApi}
        canEdit={canEdit}
        sort={(a, b) => a.dayOfWeek - b.dayOfWeek}
        columns={[
          { label: 'Day', render: (w) => WEEKDAYS[w.dayOfWeek - 1] ?? w.dayOfWeek },
          { label: 'Weeks', render: (w) => weeksLabel(w.weeksOfMonth) },
          { label: 'Active', align: 'center', render: (w) => <Chip size='small' color={w.active ? 'success' : 'default'} label={w.active ? 'Yes' : 'No'} /> },
        ]}
        emptyDraft={() => ({ dayOfWeek: 7, weeksOfMonth: 'ALL', active: true })}
        toDraft={(w) => ({ dayOfWeek: w.dayOfWeek, weeksOfMonth: w.weeksOfMonth, active: w.active })}
        validate={(d) =>
          /^(ALL|[1-5](\s*,\s*[1-5])*)$/i.test(d.weeksOfMonth.trim()) ? null : 'Weeks must be ALL or numbers 1–5 separated by commas, e.g. 2,4.'
        }
        toPayload={(d) => ({ dayOfWeek: d.dayOfWeek, weeksOfMonth: d.weeksOfMonth.trim().toUpperCase().replace(/\s+/g, ''), active: d.active })}
        renderForm={(d, set) => (
          <>
            <TextField select size='small' label='Day' value={d.dayOfWeek} onChange={(e) => set({ dayOfWeek: Number(e.target.value) })}>
              {WEEKDAYS.map((name, i) => (
                <MenuItem key={name} value={i + 1}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size='small'
              label='Weeks of the month'
              value={d.weeksOfMonth}
              onChange={(e) => set({ weeksOfMonth: e.target.value })}
              helperText='ALL, or e.g. 2,4 for the 2nd and 4th'
            />
            <FormControlLabel control={<Switch checked={d.active} onChange={(e) => set({ active: e.target.checked })} />} label='Active' />
          </>
        )}
      />
    </Stack>
  );
}
